import React from 'react';
import {
  HiOutlineBugAnt, HiOutlineLightBulb,
  HiOutlineUserPlus, HiOutlineClock,
  HiOutlineInbox, HiOutlineSparkles
} from "react-icons/hi2";
import { LoadingSpinner } from '../components';
import UserAvatar from '../components/UserAvatar/UserAvatar';
import useAdminFeedbackFacade from '../facades/useAdminFeedbackFacade';

const AdminFeedback = () => {
  const {
    activeTab,
    setActiveTab,
    feedbacks,
    loading,
    team,
    filteredData,
    handleUpdate,
    getSeverityStyling,
  } = useAdminFeedbackFacade();

  return (
    <div className="min-h-screen bg-[#F8FAFC] p-4 sm:p-8 lg:p-12 font-sans selection:bg-blue-100">
      <div className="max-w-6xl mx-auto">

        {/* --- HEADER --- */}
        <header className="mb-12 flex flex-col md:flex-row md:items-end justify-between gap-8">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-blue-600"></span>
              </span>
              <span className="text-[11px] font-black text-blue-600 uppercase tracking-widest">Live Dashboard</span>
            </div>
            <h1 className="text-4xl font-black text-slate-900 tracking-tight">Admin Console</h1>
            <p className="text-slate-500 font-medium mt-1">Monitor platform health and user feedback</p>
          </div>

          {/* --- STATS CARDS --- */}
          <div className="flex flex-wrap gap-4">
            <StatBox label="Total Reports" val={feedbacks.length} color="text-slate-900" />
            <StatBox label="Open Bugs" val={feedbacks.filter(f => f.category === 'bug' && f.status !== 'Resolved').length} color="text-red-600" />
            <StatBox label="Resolved" val={feedbacks.filter(f => f.status === 'Resolved').length} color="text-emerald-600" />
          </div>
        </header>

        {/* --- TABS --- */}
        <div className="flex items-center gap-2 mb-8 border-b border-slate-200 pb-px">
          <TabLink active={activeTab === 'bug'} icon={<HiOutlineBugAnt size={18} />} label="Bugs" onClick={() => setActiveTab('bug')} />
          <TabLink active={activeTab === 'suggestion'} icon={<HiOutlineLightBulb size={18} />} label="Suggestions" onClick={() => setActiveTab('suggestion')} />
          <TabLink active={activeTab === 'mine'} icon={<HiOutlineUserPlus size={18} />} label="My Tasks" onClick={() => setActiveTab('mine')} />
        </div>

        {/* --- FEEDBACK LIST --- */}
        {loading ? (
          <div className="flex flex-col items-center py-24 gap-4">
            <LoadingSpinner />
            <p className="text-slate-400 text-xs font-bold tracking-[0.3em] uppercase animate-pulse">Fetching Bugs and Suggestions</p>
          </div>
        ) : (
          <div className="grid gap-5">
            {filteredData.length > 0 ? (
              filteredData.map((item) => (
                <div
                  key={item._id}
                  className="group relative bg-white border border-slate-200 rounded-3xl p-6 sm:p-8 shadow-sm hover:shadow-xl hover:shadow-slate-200/50 hover:-translate-y-1 transition-all duration-300"
                >
                  <div className="flex flex-col lg:flex-row justify-between gap-8">

                    {/* --- LEFT CONTENT --- */}
                    <div className="flex-grow">

                      {/* Top Row: Badges & Date */}
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <span className={`text-[10px] font-black uppercase px-3 py-1 rounded-full border ${getSeverityStyling(item.severity)}`}>
                            {item.severity || 'Normal'}
                          </span>
                          <span className={`text-[10px] font-black uppercase px-3 py-1 rounded-full border ${item.category === 'bug' ? 'bg-rose-50 text-rose-600 border-rose-100' : 'bg-amber-50 text-amber-600 border-amber-100'
                            }`}>
                            {item.category}
                          </span>
                        </div>
                        <span className="flex items-center gap-1.5 text-xs font-bold text-slate-400">
                          <HiOutlineClock size={14} /> {new Date(item.createdAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                        </span>
                      </div>

                      {/* Main Content */}
                      <h3 className="text-xl sm:text-2xl font-black text-slate-900 mb-3 leading-tight">{item.title}</h3>
                      <p className="text-slate-600 mb-8 text-sm sm:text-base leading-relaxed">{item.description}</p>

                      {/* Bottom Row: User & Assignee */}
                      <div className="flex flex-col sm:flex-row sm:items-center gap-6 border-t border-slate-100 pt-6 mt-auto">

                        {/* Reporter */}
                        <div className="flex items-center gap-3">
                          <UserAvatar src={item.user?.profileImage} name={item.user?.name} size={36} className="ring-1 ring-slate-200" />
                          <div>
                            <p className="text-xs font-black text-slate-900">{item.user?.name || 'Anonymous'}</p>
                            <p className="text-[10px] font-bold text-slate-400">{item.user?.email || 'No email provided'}</p>
                          </div>
                        </div>

                        <div className="hidden sm:block h-8 w-px bg-slate-200"></div>

                        {/* --- FOUNDER ASSIGNMENT UI --- */}
                        <div className="flex items-center gap-4 bg-slate-50/50 px-4 py-2 rounded-2xl border border-slate-100">
                          <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Assignee</span>

                          <div className="flex items-center gap-3">
                            <div className="flex -space-x-2">
                              {team.map(f => {
                                const isAssigned = item.assignedTo?._id === f.id;
                                return (
                                  <button
                                    key={f.id}
                                    onClick={() => handleUpdate(item._id, { assignedTo: f.id, status: item.status === 'Pending' ? 'In-Progress' : item.status })}
                                    className={`h-8 w-8 rounded-full border-2 border-white flex items-center justify-center text-[10px] font-black text-white transition-all transform hover:scale-110 hover:z-10 relative group/tooltip ${isAssigned ? f.color + ' ring-2 ring-offset-2 ring-blue-400 z-10' : 'bg-slate-300 hover:bg-slate-400 grayscale hover:grayscale-0'
                                      }`}
                                  >
                                    {f.name[0]}
                                    <span className="absolute -top-10 scale-0 group-hover/tooltip:scale-100 transition-all bg-slate-900 text-white text-[10px] px-2 py-1 rounded whitespace-nowrap z-50">
                                      Assign to {f.name}
                                    </span>
                                  </button>
                                )
                              })}
                            </div>

                            <span className="text-xs font-bold text-slate-600">
                              {item.assignedTo ? (
                                item.status === 'Resolved' ? <span className="text-emerald-600">Resolved by {item.assignedTo.name}</span> : <span>Assigned to {item.assignedTo.name}</span>
                              ) : (
                                <span className="text-slate-400 italic">Unassigned</span>
                              )}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* --- RIGHT COLUMN: STATUS --- */}
                    <div className="flex flex-row lg:flex-col justify-between items-center lg:items-end gap-3 min-w-[160px] border-t lg:border-t-0 lg:border-l border-slate-100 pt-6 lg:pt-0 lg:pl-8">
                      <div className="w-full">
                        <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 lg:text-right">Ticket Status</label>
                        <select
                          value={item.status}
                          onChange={(e) => handleUpdate(item._id, { status: e.target.value })}
                          className={`w-full px-4 py-3 rounded-xl font-black text-xs sm:text-sm outline-none border-2 cursor-pointer transition-all appearance-none text-center ${item.status === 'Resolved' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                              item.status === 'In-Progress' ? 'bg-blue-50 text-blue-700 border-blue-200 shadow-[0_0_15px_rgba(59,130,246,0.15)]' :
                                item.status === 'Dismissed' ? 'bg-slate-50 text-slate-500 border-slate-200' : 'bg-white text-slate-700 border-slate-200 hover:border-slate-300'
                            }`}
                          style={{ backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M5 8l5 5 5-5'/%3e%3c/svg%3e")`, backgroundPosition: `right 0.5rem center`, backgroundRepeat: `no-repeat`, backgroundSize: `1.5em 1.5em`, paddingRight: `2.5rem` }}
                        >
                          <option value="Pending">Pending</option>
                          <option value="In-Progress">In-Progress</option>
                          <option value="Resolved">Resolved</option>
                          <option value="Dismissed">Dismissed</option>
                        </select>
                      </div>
                    </div>

                  </div>
                </div>
              ))
            ) : (
              /* --- EMPTY STATE --- */
              <div className="flex flex-col items-center justify-center py-24 bg-white rounded-3xl border-2 border-dashed border-slate-200 text-center px-4">
                <div className="h-20 w-20 bg-slate-50 rounded-full flex items-center justify-center mb-6">
                  {activeTab === 'bug' ? <HiOutlineSparkles size={32} className="text-slate-400" /> : <HiOutlineInbox size={32} className="text-slate-400" />}
                </div>
                <h3 className="text-xl font-black text-slate-900 mb-2">No {activeTab}s found</h3>
                <p className="text-slate-500 max-w-sm">Looks like your queue is empty for this section. Enjoy the silence!</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// --- SUB-COMPONENTS ---

const StatBox = ({ label, val, color }) => (
  <div className="bg-white border border-slate-200 px-6 py-5 rounded-3xl shadow-sm min-w-[140px] flex-1 sm:flex-none">
    <span className="block text-[10px] font-black uppercase text-slate-400 mb-1 tracking-widest">{label}</span>
    <span className={`text-3xl font-black ${color}`}>{val}</span>
  </div>
);

const TabLink = ({ active, label, icon, onClick }) => (
  <button
    onClick={onClick}
    className={`pb-4 px-4 sm:px-6 flex items-center gap-2 font-bold text-sm transition-all relative ${active ? 'text-blue-600' : 'text-slate-400 hover:text-slate-700'}`}
  >
    {icon}
    <span className="hidden sm:block">{label}</span>
    {active && <div className="absolute bottom-0 left-0 w-full h-1 bg-blue-600 rounded-t-full shadow-[0_-2px_8px_rgba(37,99,235,0.4)]" />}
  </button>
);

export default AdminFeedback;